import React, { useState } from "react";
import { useParams } from "react-router";
import { WizardStep } from "@sscs/types";
import { Dialog, QueryResult, Toast, Wizard } from "@sscs/ui";
import { buildRlPurl, getClassName } from "@sscs/utils";
import {
  PACKAGE_ROUTE,
  PROJECT_ROUTE,
  VERSION_SOFTWARE_INFO_DATA,
} from "Constants";
import { useProjectsRoute } from "Hooks";
import {
  PackageFormData,
  PortalFile,
  ProjectFormData,
  VersionSoftwareInfo,
} from "Types";
import {
  apiErrorToast,
  getCleanValue,
  getPackageFormSchema,
  getProjectFormSchema,
  getVersionSoftwareInfoFormSchema,
} from "Utils";
import WizardSteps from "../components/WizardSteps";
import { MoveToProjectWizardStep, WIZARD_STEPS_LABELS } from "../constants";
import { useProjects, useRestartNotification, useVersions } from "../hooks";
import { useMoveFileToProjectMutation } from "../services/moveToProject.api";
import { MoveData, MoveFileToProjectResponse } from "../types";
import { getDefaultMoveData } from "../utils";
import styles from "./moveFileToProject.module.scss";

type FormData = ProjectFormData | PackageFormData | VersionSoftwareInfo;

type MoveFileToProjectProps = {
  onClose: (isMoved?: boolean) => void;
  isOpen: boolean;
  file: PortalFile;
  linkToProject: boolean;
};

const MoveFileToProjectContent = ({
  onClose,
  file,
  linkToProject,
}: Omit<MoveFileToProjectProps, "isOpen">): React.JSX.Element => {
  const { groupId } = useParams();

  const [activeStep, setActiveStep] = useState<MoveToProjectWizardStep>(
    MoveToProjectWizardStep.SelectProjectStep,
  );
  const [moveData, setMoveData] = useState<MoveData>(
    getDefaultMoveData(file.id),
  );
  const [moveFile, { isLoading: isMoving }] = useMoveFileToProjectMutation();
  const projectsRoute = useProjectsRoute();
  const {
    projectNames,
    queryStatus: projectsQueryStatus,
    selectedProjectPackageNames,
    selectedPackageId,
    selectedProjectId,
  } = useProjects(groupId, moveData.project_name, moveData.package_name);

  const {
    maxVersionsLimit,
    pushedOutVersion,
    selectedPredecessorVersion,
    versions,
    queryStatus: versionsQueryStatus,
  } = useVersions(selectedPackageId, moveData.predecessor_id);

  const dialogContentClassName = getClassName(styles["dialog-content"], {
    [styles["elongated-content"]]: pushedOutVersion !== undefined,
  });

  useRestartNotification(
    selectedProjectId,
    selectedPackageId,
    moveData.predecessor_id,
    activeStep,
  );

  const handleNext = (data?: FormData) => {
    if (data) {
      setMoveData((previous) => ({ ...previous, ...data }));
    }

    setActiveStep((previous) => previous + 1);
  };

  const handlePrevious = (data?: FormData): void => {
    if (data) {
      setMoveData((previous) => ({ ...previous, ...data }));
    }

    setActiveStep((previous) => previous - 1);
  };

  const handleMove = async () => {
    let response: MoveFileToProjectResponse | null = null;

    try {
      const { force, replace, notify_on_completion, ...body } = moveData;

      response = await moveFile({
        body,
        force,
        replace,
        notify_on_completion,
      }).unwrap();

      const linkPath = `${projectsRoute}/${PROJECT_ROUTE}/${response.project_id}/${PACKAGE_ROUTE}/${response.package_id}`;

      Toast.show({
        title: (
          <>
            File{" "}
            <strong className={styles["success-text-emphasised"]}>
              {file.file_name}
            </strong>{" "}
            moved to{" "}
            <strong className={styles["success-text-emphasised"]}>
              {buildRlPurl(
                moveData.project_name,
                moveData.package_name,
                moveData.version,
              )}
            </strong>
          </>
        ),
        type: "success",
        actions: linkToProject
          ? [{ label: "View in project", to: linkPath }]
          : undefined,
        props: {
          autoClose: false,
        },
      });

      onClose(true);
    } catch (error) {
      apiErrorToast(error, "Could not move file");
      onClose(false);
      return;
    }
  };

  return (
    <Wizard
      title="Move file to project"
      subtitle={`Moving ${file.file_name}`}
      activeStep={activeStep}
      steps={
        [
          {
            label:
              WIZARD_STEPS_LABELS[MoveToProjectWizardStep.SelectProjectStep],
            onNext: (data: ProjectFormData) => {
              handleNext({
                ...data,
                project_name: getCleanValue(data.project_name),
              });
            },
            onPrevious: handlePrevious,
            schema: getProjectFormSchema(),
            formValues: {
              project_name: moveData.project_name,
              project_description: moveData.project_description,
            },
            component: (
              <QueryResult status={projectsQueryStatus}>
                <WizardSteps.SelectProject projects={projectNames ?? []} />
              </QueryResult>
            ),
          },
          {
            label:
              WIZARD_STEPS_LABELS[MoveToProjectWizardStep.SelectPackageStep],
            onNext: (data: PackageFormData) => {
              handleNext({
                ...data,
                package_name: getCleanValue(data.package_name),
              });
            },
            onPrevious: handlePrevious,
            schema: getPackageFormSchema(),
            formValues: {
              package_name: moveData.package_name,
              package_description: moveData.package_description,
            },
            component: (
              <QueryResult status={projectsQueryStatus}>
                <WizardSteps.SelectPackage
                  packages={selectedProjectPackageNames ?? []}
                />
              </QueryResult>
            ),
          },
          {
            label:
              WIZARD_STEPS_LABELS[MoveToProjectWizardStep.SoftwareInfoStep],
            onNext: handleNext,
            onPrevious: handlePrevious,
            schema: getVersionSoftwareInfoFormSchema(
              versions?.map((version) => version.software_info.version) ?? [],
            ),
            formValues: !moveData.version
              ? {
                  ...VERSION_SOFTWARE_INFO_DATA,
                  ...(versions[0]?.software_info ?? file.software_info),
                  release_date: null,
                  predecessor_id: versions[0]?.version_id ?? null,
                  version: "",
                }
              : { ...moveData },
            component: (
              <QueryResult status={versionsQueryStatus}>
                <WizardSteps.SoftwareInfo
                  fileName={file.file_name}
                  importedFrom={file.imported_from}
                  predecessors={versions.map((version) => ({
                    id: version.version_id,
                    createdDate: version.created,
                    predecessorId: version.predecessor_id,
                    softwareInfo: version.software_info,
                  }))}
                  maxVersionsLimit={maxVersionsLimit}
                />
              </QueryResult>
            ),
            formProps: {
              horizontal: true,
              dense: true,
            },
          },
          {
            label: WIZARD_STEPS_LABELS[MoveToProjectWizardStep.OverviewStep],
            nextStep: {
              label: `Move${pushedOutVersion ? " and Replace" : ""}`,
              isLoading: isMoving,
            },
            onNext: async () => {
              await handleMove();
            },
            onPrevious: handlePrevious,
            component: (
              <WizardSteps.Overview
                moveData={moveData}
                fileName={file.file_name}
                predecessorVersion={selectedPredecessorVersion}
                showForce={pushedOutVersion !== undefined}
                showReplace={versions.some(
                  (version) =>
                    version.software_info.version === moveData.version,
                )}
              />
            ),
          },
        ] satisfies [
          WizardStep<ProjectFormData>,
          WizardStep<PackageFormData>,
          WizardStep<VersionSoftwareInfo>,
          WizardStep,
        ]
      }
      onStepChange={setActiveStep}
      dialogContentClasses={dialogContentClassName}
    />
  );
};

const MoveFileToProject = ({
  isOpen,
  ...props
}: MoveFileToProjectProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={() => props.onClose(false)} size="md">
    <MoveFileToProjectContent {...props} />
  </Dialog.Frame>
);

export default MoveFileToProject;
